import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../Footer';
import { fetchShowcase, marketImageUrl, suggestMarket } from '../../lib/marketCatalog';
import { MARKET_CATEGORY_TREE, marketCategoryLabel, SHOW_CATEGORY_COUNTS } from '../../lib/marketCategories';
import { cartCount, onCartChange } from '../../lib/marketCart';
import { useFavorites } from '../../lib/marketFavorites';
import { compareIds, onMarketLocalChange } from '../../lib/marketLocal';
import { IMAGE_SEARCH } from '../../lib/featureFlags';
import { ACCEPTED, prepareSearchImage, stashSearchImage } from '../../lib/marketImageSearch';
import { loadSearches, rememberSearch, clearSearches } from '../../lib/marketSearchHistory';
import { sb } from '../../supabase';
import MaabarWordmark from '../MaabarWordmark';
import NotificationBell from '../NotificationBell';
import { fetchMarketState, useHioBuyOnly, keepsLegacyCatalog } from '../../lib/marketMode';
import { getSupplierPrimaryRoute } from '../../lib/supplierOnboarding';
import { SERVICES } from '../../lib/marketServices';
import { useMarketChrome } from './MarketChrome';
import { navLabel } from '../../lib/navItems';
import './market.css';
import { track } from '../../lib/growthTracker';

// The marketplace frame: one sticky bar under the site navbar — brand, the
// category menu (real product images, live counts), search with suggestions,
// and favorites / compare / orders / cart with their counts — then the page,
// then the site footer. Every market page uses it, so the way around the
// market is the same everywhere.

export const SHELL_T = {
  ar: { inquiries: 'أسئلتي للموردين', preview: 'معاينة للمشرفين — السوق غير منشور للعملاء بعد.', cats: 'الأقسام', services: 'خدمات معبر', account: 'حسابي', signIn: 'الدخول', myAccount: 'لوحتي', supplierDash: 'لوحة المورد', adminConsole: 'لوحة الإدارة',
    traderLogin: 'دخول التجار', supplierLogin: 'دخول الموردين', language: 'اللغة', signOut: 'تسجيل الخروج', addresses: 'عناويني', search: 'ابحث عن منتج بالاسم أو المواصفات', go: 'بحث', fav: 'المفضلة', cmp: 'المقارنة', orders: navLabel('purchases', 'ar'), cart: 'السلة', messages: navLabel('messages', 'ar'), quotes: navLabel('quotes', 'ar'),
    all: 'كل منتجات القسم', products: (n) => `${n} منتج`, close: 'إغلاق', everyCategory: 'تصفح كل المنتجات', byPhoto: 'ابحث بالصورة', recent: 'عمليات البحث الأخيرة', clearRecent: 'مسح',
    imgErr: { type: 'الصيغ المدعومة: JPG وPNG وWEBP', size: 'حجم الصورة أكبر من 15 ميجابايت', unreadable: 'تعذّرت قراءة الصورة' } },
  en: { inquiries: 'My supplier questions', preview: 'Admin preview — the market is not yet published to customers.', cats: 'Categories', services: 'MAABAR services', account: 'My account', signIn: 'Sign in', myAccount: 'Dashboard', supplierDash: 'Supplier dashboard', adminConsole: 'Admin console',
    traderLogin: 'Trader login', supplierLogin: 'Supplier login', language: 'Language', signOut: 'Sign out', addresses: 'My addresses', search: 'Search products by name or specification', go: 'Search', fav: 'Favorites', cmp: 'Compare', orders: navLabel('purchases', 'en'), cart: 'Cart', messages: navLabel('messages', 'en'), quotes: navLabel('quotes', 'en'),
    all: 'All products in this category', products: (n) => `${n} products`, close: 'Close', everyCategory: 'Browse all products', byPhoto: 'Search by photo', recent: 'Recent searches', clearRecent: 'Clear',
    imgErr: { type: 'Supported formats: JPG, PNG, WEBP', size: 'The photo is larger than 15 MB', unreadable: 'The photo could not be read' } },
  zh: { inquiries: '我的供应商提问', preview: '管理员预览 — 市场尚未向客户开放。', cats: '分类', services: 'MAABAR 服务', account: '我的账户', signIn: '登录', myAccount: '控制台', supplierDash: '供应商控制台', adminConsole: '管理后台',
    traderLogin: '买家登录', supplierLogin: '供应商登录', language: '语言', signOut: '退出', addresses: '我的地址', search: '按名称或规格搜索商品', go: '搜索', fav: '收藏', cmp: '对比', orders: navLabel('purchases', 'zh'), cart: '购物车', messages: navLabel('messages', 'zh'), quotes: navLabel('quotes', 'zh'),
    all: '该分类全部商品', products: (n) => `${n} 件商品`, close: '关闭', everyCategory: '浏览全部商品', byPhoto: '以图搜索', recent: '最近搜索', clearRecent: '清除',
    imgErr: { type: '支持 JPG、PNG、WEBP', size: '图片超过 15 MB', unreadable: '无法读取图片' } },
};

export const Icon = {
  Home: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1z" /></svg>,
  Grid: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></svg>,
  Services: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h10" /></svg>,
  User: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8.5" r="3.6" /><path d="M4.5 20c1.4-3.6 4.3-5.3 7.5-5.3s6.1 1.7 7.5 5.3" /></svg>,
  Camera: () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h3l1.6-2.4h6.8L17 8h3v11H4z" /><circle cx="12" cy="13.2" r="3.6" /></svg>,
  Search: () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  Heart: ({ filled } = {}) => <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z" /></svg>,
  Compare: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><path d="M7 4v16M17 4v16M4 8h6M14 16h6" /><path d="m4 8 3-4 3 4M14 16l3 4 3-4" /></svg>,
  Orders: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M6 3h12l1 5H5zM5 8h14v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z" /><path d="M9 12h6" /></svg>,
  Chat: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /></svg>,
  Cart: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 4h2l2.4 11.2a1.5 1.5 0 0 0 1.5 1.2h8.7a1.5 1.5 0 0 0 1.5-1.1L21 8H6.2" /><circle cx="9.5" cy="20" r="1.3" /><circle cx="17.5" cy="20" r="1.3" /></svg>,
  Close: () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  Chevron: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>,
  Filter: () => <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>,
};

export const Thumb = ({ refId, alt = '' }) => (refId ? <img src={marketImageUrl(refId, true)} alt={alt} loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : <span className="mk-thumb-ph" aria-hidden="true" />);

function useCounts(user) {
  const fav = useFavorites(user?.id);
  const [cmp, setCmp] = useState(() => compareIds().length);
  const [cart, setCart] = useState(() => cartCount());
  useEffect(() => onMarketLocalChange(() => setCmp(compareIds().length)), []);
  useEffect(() => onCartChange((lines) => setCart(lines.length)), []);
  return { fav: fav.ids.size, cmp, cart };
}

export function SearchBox({ lang, c, initial, onSearch, placeholder }) {
  const nav = useNavigate();
  const [q, setQ] = useState(initial || '');
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  // Recent searches (2026-10-25): shown while the box is focused and holds
  // fewer than two characters — the suggestions take over from there.
  const [recent, setRecent] = useState(() => loadSearches());
  const t = useRef(null);
  useEffect(() => { setQ(initial || ''); }, [initial]);
  useEffect(() => {
    clearTimeout(t.current);
    if (q.trim().length < 2) { setSugs([]); return undefined; }
    t.current = setTimeout(async () => { setSugs(await suggestMarket(q.trim(), lang)); setHi(-1); }, 200);
    return () => clearTimeout(t.current);
  }, [q, lang]);
  const go = (text, source = 'typed') => {
    const v = (text ?? q).trim();
    setOpen(false);
    if (v) track('search_submitted', { query: v, source, language: lang });
    if (v) setRecent(rememberSearch(v));
    if (onSearch) onSearch(v); else nav(v ? `/market/search?q=${encodeURIComponent(v)}` : '/market/search');
  };
  return (
    <form className="mk-search" role="search" onSubmit={(e) => { e.preventDefault(); go(hi >= 0 ? sugs[hi] : undefined, hi >= 0 ? 'suggestion' : 'typed'); }}>
      <input data-market-search="1" value={q} placeholder={placeholder || c.search} aria-label={placeholder || c.search} aria-autocomplete="list" dir={lang === 'ar' ? 'rtl' : 'ltr'}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || !sugs.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, sugs.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, -1)); }
          if (e.key === 'Escape') setOpen(false);
        }} />
      {IMAGE_SEARCH && <ImageSearchButton c={c} />}
      <button type="submit" aria-label={c.go}><Icon.Search /><span className="lbl">{c.go}</span></button>
      {open && q.trim().length < 2 && recent.length > 0 && (
        <ul className="mk-suggest mk-recent" role="listbox" aria-label={c.recent}>
          <li className="mk-recent-head" role="presentation" onMouseDown={(e) => e.preventDefault()}>
            <span>{c.recent}</span>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); clearSearches(); setRecent([]); }}>{c.clearRecent}</button>
          </li>
          {recent.map((s) => <li key={s} role="option" aria-selected={false} onMouseDown={() => { setQ(s); go(s, 'history'); }}>↺ {s}</li>)}
        </ul>
      )}
      {open && sugs.length > 0 && (
        <ul className="mk-suggest" role="listbox" data-suggestions={sugs.length}>
          {sugs.map((s, i) => <li key={s} role="option" aria-selected={i === hi} onMouseDown={() => { setQ(s); go(s, 'suggestion'); }}>{s}</li>)}
        </ul>
      )}
    </form>
  );
}

/** The camera in the search bar: pick or take a photo → /market/image-search. */
export function ImageSearchButton({ c, onPrepared }) {
  const nav = useNavigate();
  const ref = useRef(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    setErr(''); setBusy(true);
    const prep = await prepareSearchImage(file);
    setBusy(false);
    if (ref.current) ref.current.value = '';
    if (prep.error) { setErr(c.imgErr[prep.error] || c.imgErr.unreadable); return; }
    if (onPrepared) onPrepared(prep);
    else if (stashSearchImage(prep)) nav('/market/image-search');
  };
  return (
    <>
      <button type="button" className="mk-cam" data-image-search="1" aria-label={c.byPhoto} title={c.byPhoto} disabled={busy} onClick={() => ref.current?.click()}>
        <Icon.Camera />
      </button>
      <input ref={ref} type="file" accept={ACCEPTED.join(',')} hidden data-image-input="1" onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])} />
      {err && <span className="mk-cam-err" role="alert" data-image-error="1">{err}</span>}
    </>
  );
}

export function CategoryMenu({ lang, onClose }) {
  const nav = useNavigate();
  const c = SHELL_T[lang] || SHELL_T.ar;
  const [show, setShow] = useState(null);
  const [cur, setCur] = useState(null);
  useEffect(() => { fetchShowcase().then(setShow); }, []);
  useEffect(() => { const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const cats = MARKET_CATEGORY_TREE.filter((x) => show?.[x.code]?.total > 0).sort((a, b) => show[b.code].total - show[a.code].total);
  const active = cats.find((x) => x.code === cur) || cats[0];
  const go = (to) => { onClose(); nav(to); };
  const label = (x) => (lang === 'ar' ? x.ar : x.en);
  return (
    <>
      <div className="mk-menu-scrim" onClick={onClose} />
      <div className="mk-menu" role="dialog" aria-label={c.cats} data-category-menu="1">
        <ul className="mk-menu-list">
          {cats.map((x) => (
            <li key={x.code}>
              <button type="button" aria-current={active?.code === x.code} onMouseEnter={() => setCur(x.code)} onFocus={() => setCur(x.code)}
                onClick={() => (window.matchMedia('(max-width: 720px)').matches ? setCur(x.code) : go(`/market/c/${x.code}`))}>
                <Thumb refId={show[x.code].images?.[0]} /><span>{label(x)}</span>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{show[x.code].total}</span>}
              </button>
            </li>
          ))}
        </ul>
        {active && (
          <div className="mk-menu-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <h3>{marketCategoryLabel(active.code, lang)}</h3>
              <button type="button" className="mk-icon-btn" onClick={onClose} aria-label={c.close}><Icon.Close /></button>
            </div>
            <button type="button" className="mk-link-all" onClick={() => go(`/market/c/${active.code}`)}>{c.all}{SHOW_CATEGORY_COUNTS && ` · ${c.products(show[active.code].total)}`}</button>
            <div className="mk-menu-groups">
              {active.groups.filter((g) => show[active.code].leaves[g.code]?.count > 0).map((g) => {
                const leaf = show[active.code].leaves[g.code];
                const types = g.types.filter((t) => leaf.types?.[t.code]?.count > 0);
                return (
                  <div className="mk-menu-group" key={g.code} data-menu-sub={g.code}>
                    <button type="button" className="mk-leaf" data-menu-leaf={g.code} onClick={() => go(`/market/c/${active.code}/${g.code}`)}>
                      <Thumb refId={leaf.images?.[0]} /><span><b>{label(g)}</b>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{c.products(leaf.count)}</span>}</span>
                    </button>
                    {types.length > 0 && (
                      <ul className="mk-type-links">
                        {types.map((t) => (
                          <li key={t.code}><button type="button" data-menu-type={t.code} onClick={() => go(`/market/c/${active.code}/${g.code}/${t.code}`)}>
                            {label(t)}{SHOW_CATEGORY_COUNTS && <> <span className="mk-count">{leaf.types[t.code].count}</span></>}
                          </button></li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
            <button type="button" className="mk-link-all mk-menu-every" onClick={() => go('/market/products')}>{c.everyCategory}</button>
          </div>
        )}
      </div>
    </>
  );
}

/** A small dropdown: a button and a panel that closes on outside click and Escape. */
function Dropdown({ label, icon, children, className = '', dataKey }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', out); window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); window.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className={`mk-dd ${className}`} ref={ref}>
      <button type="button" className="mk-act mk-dd-btn" aria-expanded={open} aria-haspopup="menu" aria-label={label} data-dd={dataKey} onClick={() => setOpen((x) => !x)}>
        {icon}<span className="lbl">{label}</span>
      </button>
      {open && <div className="mk-dd-panel" role="menu" onClick={(e) => { if (e.target.closest('[data-close]')) setOpen(false); }}>{children}</div>}
    </div>
  );
}

// MAABAR's ONE header on market pages (the site navbar is not drawn there —
// App.js). Official logo, categories, search (with search by photo), the three
// services, favorites / compare / orders / cart, notifications, the account
// and the language. Not a separate "market" identity: it is MAABAR.
// hideSearch: the page carries its own search (the homepage hero, owner 2026-11-02).
export function MarketBar({ lang = 'ar', user: userProp, initialQuery, onSearch, searchPlaceholder, hideSearch = false }) {
  const nav = useNavigate();
  // sign-in comes back to where the trader was (the cart, a product…), not the dashboard (store test 2026-11-03)
  const here = useLocation();
  const backHere = `?next=${encodeURIComponent(here.pathname + here.search)}`;
  const chrome = useMarketChrome();
  const user = userProp ?? chrome.user;
  const profile = chrome.profile;
  const c = SHELL_T[lang] || SHELL_T.ar;
  const n = useCounts(user);
  const [menu, setMenu] = useState(false);
  const market = useHioBuyOnly();
  const role = String(profile?.role || '').toLowerCase();
  // The logo goes home: "/" when the marketplace IS the home page, else the market.
  const home = market.hioBuyOnly && !keepsLegacyCatalog(profile) ? '/' : '/market';
  const act = (key, to, icon, count) => (
    <button type="button" className="mk-act" data-bar={key} onClick={() => nav(to)} aria-label={c[key]}>
      {icon}<span className="lbl">{c[key]}</span>{count > 0 && <span className="mk-badge" data-count={count}>{count}</span>}
    </button>
  );
  const dashboard = role === 'admin' || role === 'super_admin' ? ['/admin2', c.adminConsole]
    : role === 'supplier' ? [getSupplierPrimaryRoute(profile, user), c.supplierDash] : ['/dashboard', c.myAccount];
  const signOut = async () => { await sb.auth.signOut(); chrome.setUser?.(null); chrome.setProfile?.(null); nav(home); };
  const services = SERVICES.map((sv) => (
    <button key={sv.key} type="button" role="menuitem" data-close="1" data-service-menu={sv.key} onClick={() => sv.go(nav)}>{sv[lang] || sv.ar}</button>
  ));
  const languages = (
    <div className="mk-dd-langs" role="group" aria-label={c.language}>
      {['ar', 'en', 'zh'].map((l) => (
        <button key={l} type="button" aria-pressed={lang === l} data-lang={l} data-close="1" onClick={() => chrome.setLang?.(l)}>{l === 'ar' ? 'العربية' : l === 'en' ? 'English' : '中文'}</button>
      ))}
    </div>
  );
  return (
    <div className={`mk-bar${hideSearch ? ' no-search' : ''}`} data-maabar-header="1">
      <div className="mk-wrap mk-bar-row">
        <button type="button" className="mk-logo" data-home-logo="1" aria-label="MAABAR" onClick={() => nav(home)}><MaabarWordmark height={22} /></button>
        <button type="button" className="mk-cats-btn" aria-expanded={menu} data-open-categories="1" onClick={() => setMenu((x) => !x)}><Icon.Grid /><span className="lbl">{c.cats}</span></button>
        <span className="mk-bar-break" aria-hidden="true" />
        {hideSearch ? <span className="mk-search-gap" aria-hidden="true" /> : <SearchBox lang={lang} c={c} initial={initialQuery} onSearch={onSearch} placeholder={searchPlaceholder} />}
        <div className="mk-actions">
          <Dropdown label={c.services} icon={<Icon.Services />} className="mk-services-dd" dataKey="services">{services}</Dropdown>
          {act('fav', '/market/favorites', <Icon.Heart />, n.fav)}
          {act('cmp', '/market/compare', <Icon.Compare />, n.cmp)}
          {act('orders', '/market/orders', <Icon.Orders />, 0)}
          {/* Messages: the one place the trader's conversations live (/inbox) —
              every other header has it, so the market bar does too. */}
          {user && role === 'buyer' && act('messages', '/inbox', <Icon.Chat />, 0)}
          {act('cart', '/market/cart', <Icon.Cart />, n.cart)}
          {user && <span className="mk-bell"><NotificationBell user={user} lang={lang} /></span>}
          <Dropdown label={user ? c.account : c.signIn} icon={<Icon.User />} className="mk-account-dd" dataKey="account">
            {user ? (
              <>
                <button type="button" role="menuitem" data-close="1" data-account-home="1" onClick={() => nav(dashboard[0])}>{dashboard[1]}</button>
                {role === 'buyer' && <button type="button" role="menuitem" data-close="1" data-account-quotes="1" onClick={() => nav('/dashboard?tab=requests')}>{c.quotes}</button>}
                <button type="button" role="menuitem" data-close="1" onClick={() => nav('/market/orders')}>{c.orders}</button>
                <button type="button" role="menuitem" data-close="1" data-account-inquiries="1" onClick={() => nav('/market/inquiries')}>{c.inquiries}</button>
                <button type="button" role="menuitem" data-close="1" data-account-addresses="1" onClick={() => nav('/market/addresses')}>{c.addresses}</button>
              </>
            ) : (
              <>
                <button type="button" role="menuitem" data-close="1" data-signin="buyer" onClick={() => nav('/login/buyer' + backHere)}>{c.traderLogin}</button>
                <button type="button" role="menuitem" data-close="1" data-signin="supplier" onClick={() => nav('/login/supplier')}>{c.supplierLogin}</button>
              </>
            )}
            <button type="button" role="menuitem" className="mk-phone-item" data-close="1" onClick={() => nav('/market/compare')}>{c.cmp}{n.cmp > 0 ? ` (${n.cmp})` : ''}</button>
            <div className="mk-dd-sep mk-phone-services"><span>{c.services}</span>{services}</div>
            <div className="mk-dd-sep"><span>{c.language}</span>{languages}</div>
            {user && <button type="button" role="menuitem" className="mk-dd-out" data-close="1" data-signout="1" onClick={signOut}>{c.signOut}</button>}
          </Dropdown>
        </div>
      </div>
      {menu && <CategoryMenu lang={lang} onClose={() => setMenu(false)} />}
    </div>
  );
}

/**
 * On every market page while the market is unpublished: says the viewer is
 * seeing an admin preview — only when the DATABASE grants it (preview =
 * is_admin() for this caller), never on the client's own idea of the role.
 */
function PreviewNote({ lang, user }) {
  const [st, setSt] = useState(null);
  useEffect(() => { let alive = true; fetchMarketState().then((v) => alive && setSt(v)); return () => { alive = false; }; }, [user?.id]);
  if (!st?.preview || st.marketplace_public) return null;
  return <div className="mk-wrap"><div className="mk-preview-note" data-admin-preview="1">{(SHELL_T[lang] || SHELL_T.ar).preview}</div></div>;
}

// stickyBar: the page has a phone sticky bar at the bottom (the product page) — room is kept for it.
export default function MarketShell({ lang = 'ar', user, initialQuery, onSearch, searchPlaceholder, children, className = '', hideSearch = false, stickyBar = false }) {
  const isAr = lang === 'ar';
  return (
    <>
      <div className={`mk-page ${isAr ? 'ar' : 'en'} ${stickyBar ? 'has-sticky' : ''} ${className}`} dir={isAr ? 'rtl' : 'ltr'}>
        <MarketBar lang={lang} user={user} initialQuery={initialQuery} onSearch={onSearch} searchPlaceholder={searchPlaceholder} hideSearch={hideSearch} />
        <PreviewNote lang={lang} user={user} />
        {children}
      </div>
      <Footer lang={lang} />
    </>
  );
}
